import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { useInitiateGoogleConnection } from '@/hooks/mutations/google-connections/use-google-connection-mutations';
import { googleConnectionsQueryKey } from '@/hooks/queries/google-connections/use-google-connections';
import { useOAuthConsentPopup } from '@/hooks/use-oauth-consent-popup';
import { listGoogleConnections } from '@/services/google-connections/google-connections';
import { GoogleService } from '@/services/google-connections/types';

import { isGoogleConsentSettled } from './helpers/connection-state';

const CONNECTED_MESSAGE: Record<GoogleService, string> = {
  gmail: 'Gmail connected',
  drive: 'Google Drive connected',
};

export const useGoogleConnectPopup = (orgId?: string) => {
  const notification = useNotification();
  const queryClient = useQueryClient();
  const initiate = useInitiateGoogleConnection(orgId);
  const consent = useOAuthConsentPopup('google-connect');

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: googleConnectionsQueryKey(orgId),
      }),
    [queryClient, orgId],
  );

  const connect = useCallback(
    async (service: GoogleService) => {
      try {
        await consent.start(
          service,
          async () => {
            const result = await initiate.mutateAsync(service);
            return {
              redirectUrl: result.redirect_url,
              isSettled: async () => {
                const fresh = await listGoogleConnections(orgId!);
                if (!isGoogleConsentSettled(fresh.services[service])) {
                  return false;
                }
                queryClient.setQueryData(
                  googleConnectionsQueryKey(orgId),
                  fresh,
                );
                return true;
              },
            };
          },
          {
            onConnected: () =>
              notification.success?.(CONNECTED_MESSAGE[service]),
            onAbandoned: () => void refresh(),
            onTimedOut: () => {
              void refresh();
              notification.error(
                'Google authorization timed out. Request a fresh link and try again.',
              );
            },
          },
        );
      } catch (error) {
        Sentry.captureException(error, { extra: { orgId, service } });
        notification.error(
          error instanceof Error
            ? error.message
            : 'Could not start Google authorization',
        );
      }
    },
    [consent, initiate, notification, orgId, queryClient, refresh],
  );

  return { connect, isConnecting: consent.isWaiting };
};
